/* eslint-disable max-len */
import * as am5 from '@amcharts/amcharts5';
import { AxisBullet, AxisLabel, AxisRenderer, AxisRendererX, AxisRendererY, CategoryAxis, ColumnSeries, IAxisRendererSettings, IColumnSeriesSettings, ValueAxis, XYChart, XYCursor } from '@amcharts/amcharts5/xy';

import { Am5Chart } from '../am5-chart.model';
import { chartColors } from '../../enums/chart-colors.enum';
import { ChartSeries } from '../chart-series.model';
import { ChartSpecificTypesEnum } from '../../../Reports/enums/chart-specific-types.enum';
import { chartSpecificTypesToBaseAxisDirectionsMap, chartSpecificTypesToValueAxisDirectionsMap } from '../../../Reports/enums/report-block-type.enum';
import { DataPointModel } from '../data-point.model';
import { StackTypesEnum } from '../barChart/category-value-x-y-chart-options.model';
import { Am5CategoryValueXYBaseChartConfiguration } from './am5-category-value-x-y-base-chart-configuration.model';
import { Am5CategoryValueXYBaseChartSettings } from './am5-category-value-x-y-base-chart-settings.model';


export class Am5CategoryValueXYBaseChart extends Am5Chart {

    declare amchart: XYChart;
    declare chartType: typeof XYChart;
    declare config: Am5CategoryValueXYBaseChartConfiguration;
    declare settings: Am5CategoryValueXYBaseChartSettings;

    categoryAxis: CategoryAxis<AxisRenderer>;
    categoryLabel: am5.Template<AxisLabel>;
    valueAxis: ValueAxis<AxisRenderer>;
    valueLabel: am5.Template<AxisLabel>;

    protected categoryAxisRenderer = AxisRenderer;

    protected pointerOverTooltipArea = false;

    constructor(
        series: ChartSeries[],
        config: Am5CategoryValueXYBaseChartConfiguration,
        settings: Am5CategoryValueXYBaseChartSettings
    ) {
        super(series, config, settings);
    }

    public initChart(element: HTMLElement, chartType = XYChart): void {
        super.initChart(element, chartType);

        this.modifyHeader();
        this.setUpFormatters();
        this.setUpCategoryAxis();
        this.setUpValueAxis();
        this.setUpCursor();
        this.setUpLegend();
        this.setUpScrollbar();
        this.setUpExporting();
    }

    modifyHeader(): void {
        if (this.config.seriesLabelOnNewLine) {
            if (this.metadataContainer) {
                this.metadataContainer.set('layout', this.root.verticalLayout);
            }
            if (this.dateRangeLabel) {
                this.dateRangeLabel.set('width', am5.p100);
            }
            if (this.primarySeriesLabel) {
                const seriesLabelContainer = this.primarySeriesLabel.parent;
                if (seriesLabelContainer && seriesLabelContainer !== this.metadataContainer) {
                    // when there's a date range, the series label sits in an absolutely positioned container on the right,
                    // which the vertical layout doesn't move
                    seriesLabelContainer.setAll({
                        position: 'relative',
                        x: 0,
                        centerX: 0,
                        paddingTop: 0,
                        width: am5.p100
                    });
                }
                this.primarySeriesLabel.set('width', am5.p100);
                this.primarySeriesLabel.set('textAlign', 'start');
            }
            this.updateSeriesLabelOnNewLineWidths();
            // runs after the base header handler, which limits both labels to half the width
            this.amchart.events.on('boundschanged', () => this.updateSeriesLabelOnNewLineWidths());
        }
    }

    private updateSeriesLabelOnNewLineWidths(): void {
        const width = this.amchart?.innerWidth();
        if (!Number.isFinite(width) || width <= 0) {
            return;
        }
        this.dateRangeLabel?.set('maxWidth', width);
        this.primarySeriesLabel?.set('maxWidth', width);
    }

    setUpFormatters(): void {
        this.amchart.root.numberFormatter.setAll({
            bigNumberPrefixes: [
                { 'number': 1e+3, 'suffix': 'K' },
                { 'number': 1e+6, 'suffix': 'M' },
                { 'number': 1e+9, 'suffix': 'B' },
                { 'number': 1e+12, 'suffix': 'T' },
                { 'number': 1e+15, 'suffix': 'Q' }
            ],
            smallNumberPrefixes: []
        });
    }

    setUpCategoryAxis(): void {
        const rendererSettings: IAxisRendererSettings = {
            minGridDistance: 1,
            cellStartLocation: 0.2,
            cellEndLocation: 0.8
        };
        const renderer = this.createCategoryAxisRenderer(rendererSettings);
        renderer.grid.template.set('visible', false);
        const categoryLabel = renderer.labels.template;
        categoryLabel.setAll({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: '0.75rem',
            interactive: true
        });
        categoryLabel.setup = (target) => {
            target.set('background', am5.Rectangle.new(this.root, {
                fill: am5.color('#ffffff'),
                fillOpacity: 0
            }));
        };
        if (this.config.hideCategoryLabel) {
            categoryLabel.set('disabled', true);
        }
        this.categoryLabel = categoryLabel;

        const axisDirection = this.amchart[chartSpecificTypesToBaseAxisDirectionsMap.get(this.settings.chartSpecificType)];
        this.categoryAxis = axisDirection.push(CategoryAxis.new(this.root, {
            renderer: renderer,
            categoryField: 'label'
        }));

        // todo: later charts
        // if (this.config.imageAsCategory) {
        //     this.categoryAxis.events.on('datavalidated', () => this.setUpImageAsCategory());
        // }
    }

    createCategoryAxisRenderer(rendererSettings: IAxisRendererSettings): AxisRendererX | AxisRendererY {
        return AxisRendererX.new(this.root, rendererSettings);
    }

    setUpValueAxis(): void {
        const renderer = this.createValueAxisRenderer({});
        const valueLabel = renderer.labels.template;
        if (this.config.showGrid) {
            renderer.setAll({
                stroke: am5.color('#ced4da'),
                strokeOpacity: 1,
                strokeWidth: 1
            });
            renderer.grid.template.set('visible', true);
            valueLabel.setAll({
                visible: true,
                paddingLeft: 0
            });
        } else {
            // todo: for SOV bar
        }

        const valueAxisSettings: any = {
            renderer: renderer,
            min: 0,
            strictMinMax: true,
            numberFormat: '#.#a'
        };

        if (this.config.stacked && this.config.stackType === StackTypesEnum.percent) {
            valueAxisSettings.max = 100;
            valueAxisSettings.calculateTotals = true;
            valueLabel.set('visible', false);
            valueLabel.adapters.add('text', (text: string) => {
                if (text && !text.endsWith('%')) {
                    return text + '%';
                }
                return text;
            });
        }
        this.valueLabel = valueLabel;

        const valueAxisDirection = this.amchart[chartSpecificTypesToValueAxisDirectionsMap.get(this.settings.chartSpecificType)];
        this.valueAxis = valueAxisDirection.push(ValueAxis.new(this.root, valueAxisSettings));

        // todo: later charts
        if (this.settings.valueAxisTitle) {
            // this.valueAxis.children.unshift(am5.Label.new(this.root, {
            //     rotation: this.isBarChart() ? 0 : -90,
            //     text: this.settings.valueAxisTitle,
            //     y: am5.p50,
            //     centerX: am5.p50
            // }));
        }
    }

    createValueAxisRenderer(rendererSettings: IAxisRendererSettings): AxisRendererX | AxisRendererY {
        return AxisRendererX.new(this.root, rendererSettings);
    }

    setUpCursor(): void {
        if (!this.config.useTooltip) {
            return;
        }

        const cursor = XYCursor.new(this.root, {
            xAxis: this.isBarChart() ? this.valueAxis : this.categoryAxis,
            yAxis: this.isBarChart() ? this.categoryAxis : this.valueAxis
        });
        cursor.lineX.set('forceHidden', true);
        cursor.lineY.set('forceHidden', true);

        this.amchart.set('cursor', cursor);
    }

    addSeries(seriesIndex: number, chartSeries: ChartSeries, data?: DataPointModel[]): void {
        const seriesSettings = {
            name: chartSeries.label,
            numberFomatter: am5.NumberFormatter.new(this.root, { smallNumberThreshold: 0 }),
            stacked: this.config.stacked
        };

        const series = this.addAm5Series(chartSeries, seriesIndex, seriesSettings);

        if (this.config.stacked) {
            // todo: stacked = true, later charts
        }

        const tooltip = this.setUpTooltip(chartSeries, seriesIndex);
        series.set('tooltip', this.config.useTooltip ? tooltip : undefined);
        if (this.config.useTooltip) {
            this.preventTooltipReplacementWhileHovered(series, tooltip);
        }
        series.columns.template.setAll({
            strokeWidth: 1,
            focusable: true,
            hoverOnFocus: true,
            interactive: true,
            tooltipText: this.config.useTooltip ? (chartSeries.tooltipText || ' ') : null,
            tooltipHTML: this.config.useTooltip ? chartSeries.tooltipHTML : null,
            tooltip: tooltip,
            tooltipX: this.getColumnTooltipX(),
            tooltipY: am5.percent(50)
        });

        const categoryRenderer = this.categoryAxis.get('renderer') as AxisRendererX | AxisRendererY;
        if (seriesIndex === 0 && !categoryRenderer.get('inside')) {
            this.setUpCategoryLabelTooltip(chartSeries);
        }

        this.setUpSeriesColor(series, chartSeries);
        this.setUpSeriesLabelText();

        const pushed = this.amchart.series.push(series);
        if (data) {
            pushed.data.setAll(data);
        }
    }

    getColumnTooltipX(): number | am5.Percent {
        return am5.percent(50);
    }

    addAm5Series(chartSeries: ChartSeries, seriesIndex: number, settings: any): ColumnSeries {
        return ColumnSeries.new(this.root, {} as any);
    }

    setUpTooltip(series: ChartSeries, seriesIndex: number): am5.Tooltip {
        const tooltip = am5.Tooltip.new(this.root, {
            getFillFromSprite: false,
            labelHTML: series.tooltipHTML,
            hoverOnFocus: true,
            keepTargetHover: true,
            autoTextColor: false
        });
        tooltip.label.set('fill', am5.color(chartColors.agprBlack));
        (tooltip.get('background') as am5.PointedRectangle).setAll({
            fill: am5.color(chartColors.agprWhite),
            fillOpacity: 1,
            stroke: am5.color('#ced4da'),
            cornerRadius: 8
        });
        tooltip.events.on('click', (event) => {
            this.tooltipLinkClicked$.next((event.target as am5.Tooltip).label.dataItem.dataContext?.['viewUrl' + seriesIndex]);
        });

        tooltip.adapters.add('pointTo', (pointTo, target) => {
            const graphics = (target.label.dataItem as any)?.get('graphics') as am5.Graphics;
            if (graphics) {
                return graphics.toGlobal(graphics._getTooltipPoint());
            }
            return pointTo;
        });

        return tooltip;
    }

    preventTooltipReplacementWhileHovered(series: ColumnSeries, tooltip: am5.Tooltip): void {
        const background = tooltip.get('background') as am5.PointedRectangle;
        background.events.on('pointerover', () => {
            this.pointerOverTooltipArea = true;
        });
        background.events.on('pointerout', () => {
            this.pointerOverTooltipArea = false;
        });
        tooltip.label.onPrivate('htmlElement', (htmlElement) => {
            if (!htmlElement) {
                return;
            }
            htmlElement.addEventListener('pointerover', () => {
                this.pointerOverTooltipArea = true;
            });
            htmlElement.addEventListener('pointerout', () => {
                this.pointerOverTooltipArea = false;
            });
        });

        const originalShowDataItemTooltip = series.showDataItemTooltip.bind(series);
        series.showDataItemTooltip = (dataItem) => {
            if (this.pointerOverTooltipArea) {
                return;
            }
            originalShowDataItemTooltip(dataItem);
        };
    }

    setUpCategoryLabelTooltip(chartSeries: ChartSeries): void {
        const categorySeries: ChartSeries = {
            ...chartSeries,
            tooltipText: (chartSeries.tooltipText || ' ').split('{label}').join('{category}'),
            tooltipHTML: chartSeries.tooltipHTML?.split('{label}').join('{category}')
        };
        const categoryTooltip = this.setUpTooltip(categorySeries, 0);
        this.categoryAxis.labelsContainer.set('tooltip', this.config.useTooltip ? categoryTooltip : undefined);

        const categoryLabel = this.categoryAxis.get('renderer').labels.template;
        categoryLabel.setAll({
            tooltipText: this.config.useTooltip ? categorySeries.tooltipText : undefined,
            tooltipHTML: this.config.useTooltip ? categorySeries.tooltipHTML : undefined
        });
    }

    setUpSeriesColor(series: ColumnSeries, chartSeries: ChartSeries): void {
        if (this.config.useColorFromData) {
            // TODO: will replace the old SOV color logic, don't remove
            //     series.columns.template.adapters.add('fill', (fill, target) => {
            //         const key = (target.dataItem?.dataContext as DataPointModel)?.['key'];
            //         let colorStr = this.settings.colorMap?.[key] || this.settings.colorMap?.['barColor'] || chartColors.agprBlue;
            //         if (this.config.useReportSeriesColor) {
            //             const columnIndex = (this.rawData || []).findIndex((x: DataPointModel) => x['key'] === key);
            //             if (columnIndex > -1) {
            //                 const adjustedIndex = (this.config.reverseBarSeriesOrder || this.config.reverseSourceOrder)
            //                     ? (this.rawData.length - 1 - columnIndex)
            //                     : columnIndex;
            //                 const seriesColor = this.config.seriesColorMapKey
            //                     ? this.settings.colorMap?.[this.config.seriesColorMapKey + (adjustedIndex + 1)]
            //                     : this.settings.colorMap?.['seriesColor' + adjustedIndex];
            //                 if (seriesColor) {
            //                     colorStr = seriesColor;
            //                 }
            //             }
            //         }
            //         target.set('stroke', am5.color(colorStr));
            //         return am5.color(colorStr);
            //     });
        } else {
            const color = am5.color(this.getSeriesColor(chartSeries));
            series.columns.template.setAll({
                fill: color,
                stroke: color
            });
        }
    }

    setUpSeriesLabelText(): void {
        // todo: screen reader text, later
    }

    setUpLegend(): void {
        // todo: legend, later charts
    }

    setUpImageAsCategory(): void {
        // todo: social coverage of network
    }

    setUpScrollbar(): void {
        // todo: for ga chart
    }

    public updateData(data: DataPointModel[]): void {
        this.rawData = data || [];

        if (!this.amchart) {
            return;
        }
        if (!data) {
            this.dataUpdated$.next(true);
            return;
        }

        const d = this.modifyDataArray();

        if (this.isContentEmpty()) {
            this.showNoContentMessage();
        }
        this.setUpSeries(d);

        this.dataUpdated$.next(true);
    }

    modifyDataArray(): DataPointModel[] {
        if (!this.rawData?.length) {
            return [];
        }

        const d: DataPointModel[] = JSON.parse(JSON.stringify(this.rawData));

        return d;
    }


    setUpSeries(data: DataPointModel[]): void {
        this.amchart.series.clear();
        this.setUpDataPointSeriesColor(data);
        this.categoryAxis.data.setAll(data);

        if (this.config.reverseBarSeriesOrder && this.isBarChart()) {
            for (let i = this.series.length - 1; i >= 0; i--) {
                this.addSeriesFromData(data, i, this.series[i]);
            }
            if (this.legend) {
                (this.legend as any).set('reverseOrder', true);
            }
        } else {
            for (let i = 0; i < this.series.length; i++) {
                this.addSeriesFromData(data, i, this.series[i]);
            }
            if (this.legend) {
                (this.legend as any).set('reverseOrder', false);
            }
        }

        if (this.legend) {
            this.legend.data.setAll(this.amchart.series.values);
        }
    }

    setUpDataPointSeriesColor(data: DataPointModel[]): void {
        const chartSeries = this.series[0];
        if (!chartSeries) {
            return;
        }
        const color = this.getSeriesColor(chartSeries);
        data.forEach(dataPoint => dataPoint.color = color);
    }

    addSeriesFromData(data: DataPointModel[], index: number, series: ChartSeries): void {
        const value = data.reduce((x, y) => x + (y['value' + index] as number || 0), 0);
        if (value) {
            this.addSeries(index, series, data);
        }
    }

    public setUpExporting(): void {
        this.exporting.events.on('exportstarted', () => {
            this.amchart.get('scrollbarX')?.hide(0);
            this.categoryLabel.set('fontSize', '0.875rem');
        });
    }

    protected override restoreExportState(): void {
        this.amchart.get('scrollbarX')?.show(0);
        this.categoryLabel.set('fontSize', '0.75rem');
    }

    async export(type: string, fileName: string): Promise<void> {
        this.exportData = this.rawData as DataPointModel[];
        await super.export(type, fileName);
    }

    public isContentEmpty(): boolean {
        return !this.rawData || this.rawData.length === 0;
    }

    public isBarChart(): boolean {
        return this.settings.chartSpecificType === ChartSpecificTypesEnum.BAR ||
            this.settings.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }

    public isColumnChart(): boolean {
        return this.settings.chartSpecificType === ChartSpecificTypesEnum.COLUMN ||
            this.settings.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
    }

    public clone(): Am5CategoryValueXYBaseChart {
        const clone = new Am5CategoryValueXYBaseChart(
            this.series.map(s => JSON.parse(JSON.stringify(s))),
            this.config,
            this.settings
        );

        clone.series.forEach((s, index) => {
            s.getSeriesUrl = this.series[index].getSeriesUrl?.bind(s);
        });

        return clone;
    }

    destroy(): void {
        super.destroy();
        this.categoryAxis = null;
        this.valueAxis = null;
        this.legend = null;
    }

    getSeriesColor(chartSeries: ChartSeries): string {
        const colorMapKey = chartSeries.colorMapKey || chartSeries.key || 'seriesColor0';
        return this.settings.colorMap?.[colorMapKey] || '#444444';
    }
}
